import React, { useEffect, useMemo, useState } from "react";
import ToolShell from "./ToolShell";
import { MessageSquareText, Search, Volume2 } from "lucide-react";
import { fetchPhrasebook } from "../../lib/api";
import { useLocale } from "../../i18n";

export default function PhrasebookTool() {
  const { t, locale } = useLocale();
  const [phrases, setPhrases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    fetchPhrasebook().then(({ phrases, categories }) => {
      setPhrases(phrases);
      setCategories(categories);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let list = phrases;
    if (cat !== "all") list = list.filter(p => p.category === cat);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(p => p.ro.toLowerCase().includes(s) || p.en.toLowerCase().includes(s));
    }
    return list;
  }, [phrases, cat, q]);

  const speak = (text) => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "ro-RO";
      u.rate = 0.85;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch (err) {
      /* speech synthesis unsupported */
      void err;
    }
  };

  return (
    <ToolShell
      title={t("tools.phrasebook")}
      subtitle={t("tools.phrasebook_sub")}
      icon={MessageSquareText}
      testid="tool-phrasebook"
      seoPath="/tools/phrasebook"
      seoTitle={locale === "ro" ? "Frazele esențiale de română" : "Essential Romanian phrasebook"}
      seoDescription={locale === "ro" ? "Peste 40 de fraze esențiale în română cu pronunție și sinteză vocală. Salutări, direcții, mâncare, urgențe." : "40+ essential Romanian phrases with pronunciation and speech synthesis. Basics, directions, food, emergencies."}
    >
      <div className="grid lg:grid-cols-12 gap-6 mb-8">
        <div className="lg:col-span-6 relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--wr-ink-2)]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={locale === "ro" ? "Caută (română sau engleză)" : "Search (Romanian or English)"}
            className="w-full pl-11 pr-4 py-3 border border-[var(--wr-border)] bg-[var(--wr-surface)] focus:border-[var(--wr-ink)] focus:outline-none font-mono-wr text-sm"
            data-testid="phrasebook-search"
          />
        </div>
        <div className="lg:col-span-6 flex items-center gap-2 flex-wrap" data-testid="phrasebook-categories">
          <button
            onClick={() => setCat("all")}
            className={`px-3 py-2 border font-mono-wr text-[11px] uppercase tracking-widest transition-colors ${
              cat === "all" ? "bg-[var(--wr-ink)] text-[var(--wr-bg)] border-[var(--wr-ink)]" : "border-[var(--wr-border)] text-[var(--wr-ink)] hover:border-[var(--wr-ink)]"
            }`}
            data-testid="phrasebook-cat-all"
          >{locale === "ro" ? "Toate" : "All"}</button>
          {categories.map(c => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`phrasebook-cat-${c.toLowerCase()}`}
              className={`px-3 py-2 border font-mono-wr text-[11px] uppercase tracking-widest transition-colors ${
                cat === c ? "bg-[var(--wr-ink)] text-[var(--wr-bg)] border-[var(--wr-ink)]" : "border-[var(--wr-border)] text-[var(--wr-ink)] hover:border-[var(--wr-ink)]"
              }`}
            >{c}</button>
          ))}
        </div>
      </div>

      <div className="border-t border-[var(--wr-border)]">
        {filtered.map((p, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-4 py-6 border-b border-[var(--wr-border)] items-start" data-testid={`phrase-${i}`}>
            <p className="md:col-span-1 font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">
              {p.category}
            </p>
            <p className="md:col-span-5 font-editorial text-2xl leading-tight text-[var(--wr-ink)]">
              {p.ro}
            </p>
            <p className="md:col-span-4 text-[15px] text-[var(--wr-ink-2)] leading-snug">
              {p.en}
            </p>
            <div className="md:col-span-2 flex items-center gap-3">
              <p className="font-mono-wr text-[11px] text-[var(--wr-red)] flex-1">/ {p.pron}</p>
              <button
                onClick={() => speak(p.ro)}
                className="p-2 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] transition-colors"
                data-testid={`phrase-speak-${i}`}
                aria-label="pronounce"
              >
                <Volume2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="py-16 text-center text-[var(--wr-ink-2)] font-mono-wr text-sm">
            {locale === "ro" ? "Nicio frază găsită." : "No phrases found."}
          </p>
        )}
      </div>
    </ToolShell>
  );
}
